import React, { useState } from "react";
import { Button, Stack } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import SearchBar from "../../components/search";
import HubButton from "../../components/button";
import { ServiceManager } from "../../services/ServiceManager";
import SimpleTable from "../../components/simpletable";
import HUBSpinner from "../../components/spinner";
import { APIStatusCode } from "../../app/enums";
import IAccount from "../../models/Account";
import CustomModal from "../../components/modal";
import IconElement from "../../components/icon";
import { SOMETHING_WENT_WRONG } from "../../app/constants";
import { useNavigate } from "react-router-dom";

interface IEstablishmentCreate {
    createEstablishment?: any
}

export default function EstablishmentCreate(props: IEstablishmentCreate) {

    const handleCloseSaveModal = () => setShowSaveModal(false);

    const [showSaveModal, setShowSaveModal] = useState(false);
    const [isSpinner, setSpinner] = useState<boolean>();
    const [searchValue, setSearchValue] = useState<string>();
    const [establishments, setEstablishments] = useState<IAccount[]>();
    let navigate = useNavigate();

    const columnHelper = createColumnHelper<IAccount>();
    const columns = [
        columnHelper.accessor('salesForceExternalID', {
            header: () => <span>External ID</span>,
            size: 25,
            cell: ({ row }) => (
                <a href={row.original.salesforceUrl} className="underline" target="_blank" rel="noreferrer">
                    {row.original.salesForceExternalID}
                </a>
            )
        }),
        columnHelper.accessor('name', {
            header: () => <span>Name</span>,
            size: 25
        }),
        columnHelper.accessor('city', {
            header: () => <span>City</span>,
            size: 25
        }),
        columnHelper.accessor('postCode', {
            header: () => <span>PostCode</span>,
            size: 25
        })
    ];

    async function getEstablishmentInternal() {
        const establishmentData = await ServiceManager.EstablishmentService.GetByAccountId(searchValue, false);
        if (establishmentData && establishmentData.data && establishmentData.data.enabled) {
            ServiceManager.ToastServer.showError("An establishment already exists for the account specified");
            return false;
        }
        else {
            if (establishmentData &&
                ((establishmentData.response && establishmentData.response.status === APIStatusCode.NotFound)
                    || (establishmentData.data && !establishmentData.data.enabled))
            ) {
                return true;
            }
            else {
                let error = ""
                if (establishmentData.response) {
                    let errors = Object.values(establishmentData.response.data.errors)
                    error = errors.toString();
                }
                else {
                    error = establishmentData.message;
                }
                ServiceManager.ToastServer.showError(error);
                return false;
            }
        }
    }

    async function getEstablishmentSalesforce() {
        const establishmentData = await ServiceManager.AuthService.GetByExternalId(searchValue);
        if (establishmentData && establishmentData.data) {
            let establishments: IAccount[] = [];
            establishments.push(establishmentData.data);
            setEstablishments(establishments);
        }
        else {
            let error = ""
            if (establishmentData.response) {
                if (establishmentData.response.data.errors) {
                    let errors = Object.values(establishmentData.response.data.errors)
                    error = errors.toString();
                }
                else if (establishmentData.response.data.Title) {
                    error = establishmentData.response.data.Title;
                }
                else {
                    error = establishmentData.response.data.title;
                    if (error === "Not Found") {
                        error = "No establishment exists with the account ID specified in Auth SSO"
                    }
                }
            }
            else {
                error = establishmentData.message;
            }
            ServiceManager.ToastServer.showError(error ?? "Auth SSO Error");
            return false;
        }
    }

    async function fetchEstablishmentData() {
        setSpinner(true);
        if (searchValue) {
            const internalEstablishmentRsp = await getEstablishmentInternal();
            if (internalEstablishmentRsp) {
                getEstablishmentSalesforce();
            }
            setSpinner(false);
        }
        else {
            ServiceManager.ToastServer.showError("Enter Salesforce External ID");
            setSpinner(false);
        }
    }

    function clickCancel() {
        setEstablishments(undefined);
    }

    async function saveEstablishments() {
        setSpinner(true);
        let response = await ServiceManager.EstablishmentService.Synchronise(establishments?.at(0)?.salesForceExternalID);
        if (response && response.data) {
            if (response.status === APIStatusCode.Ok || response.status === APIStatusCode.Created) {
                ServiceManager.ToastServer.showSuccess("Successfuly added new establishment");
                props.createEstablishment(false);
                setShowSaveModal(false);
                navigate(`/establishment/${response?.data?.establishmentID.toString()}`)
            }
            else {
                ServiceManager.ToastServer.showError("Error while adding new establishment");
            }
        }
        else {
            if (response.response) {
                if (response.response.status === APIStatusCode.NotFound) {
                    let errorMessage = response.response.data.errors.errors;
                    ServiceManager.ToastServer.showError(response.response.data.errors ? errorMessage.toString() : "No establishment exists with the account ID specified in salesforce");
                    clickCancel();
                }
                else if (response.response.data) {
                    if (response.response.data?.error) {
                        let errors = Object.values(response.response.data.errors)
                        ServiceManager.ToastServer.showError(`${errors.join('\r\n')}' '`);
                    }
                    else if (response.response.data?.title) {
                        ServiceManager.ToastServer.showError(response.response.data?.title);
                    }
                    else {
                        ServiceManager.ToastServer.showError(SOMETHING_WENT_WRONG);
                    }
                }
            }
            else {
                ServiceManager.ToastServer.showError(response.message);
            }
        }
        setSpinner(false);
    }

    const SaveConfirmationForm = () => {
        return (
            <Stack>
                <h5 className="text-center">Create account</h5>
                <p className="text-center">Do you want to create this account and all child account records.</p>
                <Stack direction="horizontal" className="mt-3">
                    {
                        (isSpinner === true)
                            ?
                            <Stack>
                                <HUBSpinner color="crbc-color" />
                            </Stack>
                            :
                            <React.Fragment>
                                <Button variant="popup-btn right-margin10 btn-outline-secondary w-50" onClick={handleCloseSaveModal}>
                                    Cancel
                                </Button>
                                <Button variant="popup-btn btn-primary crbc-bg-color w-50" onClick={saveEstablishments}>
                                    Confirm
                                </Button>
                            </React.Fragment>
                    }
                </Stack>
            </Stack>
        )
    }

    return (
        <React.Fragment>
            <label className="mt-3 label-text">Enter Salesforce External ID </label>
            <Stack direction="horizontal">
                <SearchBar placeholder="Acc-xxxx" className="w-25" onChange={(searchText: string) => setSearchValue(searchText)} />
                <HubButton disabled={!searchValue} text="Search" className="popup-btn btn btn-primary crbc-bg-color" FnOnClick={() => fetchEstablishmentData()} />
            </Stack>
            {
                (isSpinner === true)
                    ?
                    <Stack className="mt-5">
                        <HUBSpinner color="crbc-color" />
                    </Stack>
                    : null
            }

            {
                (establishments !== undefined)
                    ?
                    <div id="createEstablishment" className="mt-3 ms-2">
                        <SimpleTable
                            tableKey={"establishments"}
                            columns={columns}
                            data={establishments}
                        />
                        <Stack direction="horizontal" className="mt-5">
                            <HubButton disabled={isSpinner} text="Cancel" className="popup-btn btn btn-outline-secondary me-2" FnOnClick={() => clickCancel()} />
                            <HubButton disabled={isSpinner} text="Create" className="popup-btn btn btn-primary crbc-bg-color" FnOnClick={() => setShowSaveModal(true)} />
                        </Stack>
                    </div>
                    :
                    null
            }

            <CustomModal isShow={showSaveModal} handleClose={handleCloseSaveModal} header={<IconElement iconType={faInfoCircle} headerClass="blue-icon ms-6" color="#0060DF" />} size="md">
                <SaveConfirmationForm />
            </CustomModal>

        </React.Fragment>
    );
}